import { useEffect, useRef } from 'react';
import styles from './GlobalBackground.module.scss';

const DEBUG_ATTR = 'data-debug-bg-solid';
const ROOT_DEBUG_ATTR = 'data-bg-debug-enabled';
const POINTER_SPOTLIGHT_ATTR = 'data-bg-spotlight';

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function subscribeMediaQuery(mediaQueryList, listener) {
  if (typeof mediaQueryList.addEventListener === 'function') {
    mediaQueryList.addEventListener('change', listener);
    return () => {
      mediaQueryList.removeEventListener('change', listener);
    };
  }

  mediaQueryList.addListener(listener);
  return () => {
    mediaQueryList.removeListener(listener);
  };
}

function isTransparentColor(colorValue) {
  if (typeof colorValue !== 'string') {
    return true;
  }

  const value = colorValue.trim().toLowerCase();
  if (!value || value === 'transparent') {
    return true;
  }

  if (value === 'rgba(0, 0, 0, 0)' || value === 'rgb(0, 0, 0, 0)') {
    return true;
  }

  if (value === 'oklab(0 0 0 / 0)' || value === 'oklch(0 0 0 / 0)') {
    return true;
  }

  if (/\/\s*0(?:\.0+)?\)$/.test(value)) {
    return true;
  }

  return false;
}

function hasVisualBackground(style) {
  const image = typeof style?.backgroundImage === 'string' ? style.backgroundImage : 'none';
  const color = typeof style?.backgroundColor === 'string' ? style.backgroundColor : 'transparent';

  return image !== 'none' || !isTransparentColor(color);
}

function shouldInspectElement(node) {
  if (!(node instanceof HTMLElement)) {
    return false;
  }

  if (node.dataset.bgDebugIgnore === 'true') {
    return false;
  }

  const tagName = node.tagName;
  if (
    tagName === 'HTML' ||
    tagName === 'BODY' ||
    tagName === 'SCRIPT' ||
    tagName === 'STYLE' ||
    tagName === 'LINK' ||
    tagName === 'META' ||
    tagName === 'TITLE' ||
    tagName === 'NOSCRIPT'
  ) {
    return false;
  }

  return true;
}

function clearBackgroundDebugMarks() {
  if (typeof document === 'undefined') {
    return;
  }

  document.querySelectorAll(`[${DEBUG_ATTR}]`).forEach((element) => {
    element.removeAttribute(DEBUG_ATTR);
  });
}

export default function GlobalBackground({ debugEnabled = false, enablePointerSpotlight = false }) {
  const backgroundRef = useRef(null);

  useEffect(() => {
    if (typeof document === 'undefined') {
      return undefined;
    }

    if (debugEnabled) {
      document.documentElement.setAttribute(ROOT_DEBUG_ATTR, '1');
    } else {
      document.documentElement.removeAttribute(ROOT_DEBUG_ATTR);
      clearBackgroundDebugMarks();
    }

    return () => {
      document.documentElement.removeAttribute(ROOT_DEBUG_ATTR);
      clearBackgroundDebugMarks();
    };
  }, [debugEnabled]);

  useEffect(() => {
    if (!debugEnabled || typeof window === 'undefined' || typeof document === 'undefined') {
      return undefined;
    }

    let rafId = 0;
    let disposed = false;

    const runScan = () => {
      rafId = 0;
      if (disposed) {
        return;
      }

      const nextMarked = new Set();
      const allNodes = document.querySelectorAll('body *');

      allNodes.forEach((node) => {
        if (!shouldInspectElement(node)) {
          return;
        }

        const style = window.getComputedStyle(node);
        const marked = hasVisualBackground(style);

        if (marked) {
          node.setAttribute(DEBUG_ATTR, '1');
          nextMarked.add(node);
          return;
        }

        node.removeAttribute(DEBUG_ATTR);
      });

      document.querySelectorAll(`[${DEBUG_ATTR}]`).forEach((node) => {
        if (!nextMarked.has(node)) {
          node.removeAttribute(DEBUG_ATTR);
        }
      });
    };

    const scheduleScan = () => {
      if (rafId > 0) {
        return;
      }

      rafId = window.requestAnimationFrame(runScan);
    };

    const observer = new MutationObserver(scheduleScan);
    observer.observe(document.body, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['class', 'style', 'data-theme', 'hidden', 'aria-hidden'],
    });

    window.addEventListener('resize', scheduleScan);
    scheduleScan();

    return () => {
      disposed = true;
      observer.disconnect();
      window.removeEventListener('resize', scheduleScan);
      if (rafId > 0) {
        window.cancelAnimationFrame(rafId);
      }
      clearBackgroundDebugMarks();
    };
  }, [debugEnabled]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const backgroundNode = backgroundRef.current;
    if (!(backgroundNode instanceof HTMLElement)) {
      return undefined;
    }

    backgroundNode.removeAttribute(POINTER_SPOTLIGHT_ATTR);
    backgroundNode.style.removeProperty('--bg-spotlight-x');
    backgroundNode.style.removeProperty('--bg-spotlight-y');
    backgroundNode.style.removeProperty('--bg-spotlight-opacity');

    if (!enablePointerSpotlight) {
      return undefined;
    }

    backgroundNode.setAttribute(POINTER_SPOTLIGHT_ATTR, '1');

    const pointerMedia = window.matchMedia('(pointer: fine)');
    const reducedMotionMedia = window.matchMedia('(prefers-reduced-motion: reduce)');
    let rafId = 0;
    let disposed = false;
    let pointerX = window.innerWidth * 0.5;
    let pointerY = window.innerHeight * 0.2;

    const applyStaticSpotlight = () => {
      backgroundNode.style.setProperty('--bg-spotlight-x', '50%');
      backgroundNode.style.setProperty('--bg-spotlight-y', '20%');
      backgroundNode.style.setProperty('--bg-spotlight-opacity', '0.3');
    };

    const applyPointerSpotlight = () => {
      const viewportWidth = Math.max(window.innerWidth, 1);
      const viewportHeight = Math.max(window.innerHeight, 1);
      const xPercent = clamp((pointerX / viewportWidth) * 100, 0, 100).toFixed(2);
      const yPercent = clamp((pointerY / viewportHeight) * 100, 0, 100).toFixed(2);

      backgroundNode.style.setProperty('--bg-spotlight-x', `${xPercent}%`);
      backgroundNode.style.setProperty('--bg-spotlight-y', `${yPercent}%`);
      backgroundNode.style.setProperty('--bg-spotlight-opacity', '0.44');
    };

    const shouldTrackPointer = () => pointerMedia.matches && !reducedMotionMedia.matches;

    const flushPointerFrame = () => {
      rafId = 0;
      if (disposed || !shouldTrackPointer()) {
        return;
      }

      applyPointerSpotlight();
    };

    const schedulePointerFrame = () => {
      if (rafId > 0) {
        return;
      }

      rafId = window.requestAnimationFrame(flushPointerFrame);
    };

    const onPointerMove = (event) => {
      if (!shouldTrackPointer()) {
        return;
      }

      if (event.pointerType && event.pointerType !== 'mouse') {
        return;
      }

      pointerX = event.clientX;
      pointerY = event.clientY;
      schedulePointerFrame();
    };

    const onPointerLeave = () => {
      if (!shouldTrackPointer()) {
        return;
      }

      backgroundNode.style.setProperty('--bg-spotlight-opacity', '0.28');
    };

    const onViewportResize = () => {
      if (!shouldTrackPointer()) {
        applyStaticSpotlight();
        return;
      }

      pointerX = window.innerWidth * 0.5;
      pointerY = window.innerHeight * 0.2;
      applyPointerSpotlight();
    };

    const refreshSpotlightMode = () => {
      if (shouldTrackPointer()) {
        applyPointerSpotlight();
        return;
      }

      if (rafId > 0) {
        window.cancelAnimationFrame(rafId);
        rafId = 0;
      }

      applyStaticSpotlight();
    };

    const unsubscribePointerMedia = subscribeMediaQuery(pointerMedia, refreshSpotlightMode);
    const unsubscribeMotionMedia = subscribeMediaQuery(reducedMotionMedia, refreshSpotlightMode);

    window.addEventListener('pointermove', onPointerMove, { passive: true });
    window.addEventListener('pointerleave', onPointerLeave, { passive: true });
    window.addEventListener('blur', onPointerLeave);
    window.addEventListener('resize', onViewportResize);

    refreshSpotlightMode();

    return () => {
      disposed = true;
      unsubscribePointerMedia();
      unsubscribeMotionMedia();
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerleave', onPointerLeave);
      window.removeEventListener('blur', onPointerLeave);
      window.removeEventListener('resize', onViewportResize);
      if (rafId > 0) {
        window.cancelAnimationFrame(rafId);
      }
      backgroundNode.removeAttribute(POINTER_SPOTLIGHT_ATTR);
      backgroundNode.style.removeProperty('--bg-spotlight-x');
      backgroundNode.style.removeProperty('--bg-spotlight-y');
      backgroundNode.style.removeProperty('--bg-spotlight-opacity');
    };
  }, [enablePointerSpotlight]);

  return (
    <div
      ref={backgroundRef}
      className={styles.globalBackground}
      aria-hidden="true"
      data-bg-debug-ignore="true"
    />
  );
}
